import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppService } from 'src/modules/base/shared/services/app.service';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { AccountContextService } from 'src/modules/base/shared/services/account-context.service';
import { AuthenticationService } from 'src/modules/base/shared/services/authentication.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-kx-ai-chat-embed',
  templateUrl: './kx-ai-chat-embed.component.html',
  styleUrls: ['./kx-ai-chat-embed.component.scss'],
  standalone: false
})
export class KxAiChatEmbedComponent implements OnInit, OnDestroy {
  @ViewChild('chatElement') chatElement?: ElementRef<HTMLElement>;

  public shouldShowChat = false;
  private flowId: string | null = null;
  // The account the embed token is minted for (its client_id claim); serviceworkflow mints it for
  // the workflow mapped to the user's role, so this page holds no embed client secret.
  private clientId: string | null;
  private readonly apiBaseUrl: string;
  private readonly bundleBaseUrl: string;
  private readonly materialIconsHref = 'https://fonts.googleapis.com/icon?family=Material+Icons';
  // Same localStorage key the <kx-ai-chat> widget bundle and get-started.component.ts use —
  // keep this in sync with chat-embed-element.ts's own 'JWT_Token' key.
  private readonly jwtTokenStorageKey = 'JWT_Token';
  // Companion to the above, written by the widget's own ChatService when create_session() returns
  // a session_refresh_token. Mirrors CHAT_REFRESH_TOKEN_KEY in the widget's
  // base/service/embed-refresh-endpoint.ts — keep the two in sync.
  private readonly chatRefreshTokenStorageKey = 'JWT_Refresh_Token';
  private userId = '';
  private lastUserId: string | null = null;
  // Monotonically increasing "ticket" for the current initialization attempt. Replaces a naive
  // in-flight boolean guard, which could get stuck true forever (e.g. an attempt hanging on a
  // network call for a user who has since logged out) and silently block every future login in
  // the same tab. Whoever holds the latest generation is authoritative; any attempt that finds
  // the counter has moved on after an await knows it's been superseded and bails out.
  private initGeneration = 0;
  private initialized = false;
  private authSubscription?: Subscription;
  private routerSubscription?: Subscription;

  constructor(
    private readonly baseService: BaseService,
    private readonly appService: AppService,
    private readonly cdr: ChangeDetectorRef,
    private readonly accountContextService: AccountContextService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router
  ) {
    const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
    this.apiBaseUrl = isLocalhost
      ? '/agentic/'
      : `${window.location.origin}/agentic/`;
    this.bundleBaseUrl = isLocalhost
      ? 'http://localhost:4601/'
      : `${window.location.origin}/chat-dialog-element/`;
  }

  async ngOnInit(): Promise<void> {
    // This component lives at the app-shell level (see app.component.html), mounted per
    // session behind an isLoggedIn gate: created once when the user logs in, destroyed on
    // logout, and — crucially — never torn down by route navigation the way it was when
    // hosted inside BaseComponent.
    //
    // Subscribe BEFORE running the initial attempt below, so a slow/stuck first attempt can
    // never delay (or block) reacting to a subsequent login/logout — the two are independent.
    this.lastUserId = this.appService.getCurrentUser()?.user_id || null;
    this.authSubscription = this.authenticationService.currentUser.subscribe((user: any) => {
      const userId = user?.user_id || null;
      if (userId === this.lastUserId) {
        return;
      }
      this.lastUserId = userId;
      // Only reset here — initialization itself is left to the NavigationEnd trigger below,
      // which fires at a point where config/bot_id is actually ready. Kicking off an init
      // from here as well would race that trigger and duplicate the mapping fetch.
      this.resetChatState();
    });

    // Primary init trigger. Every completed navigation re-checks whether the chat can be set
    // up yet, until it succeeds once (`initialized`). This is deliberately not driven off the
    // login event: login fires currentUser BEFORE the GET_MY_CONFIG round-trip that populates
    // bot_id, whereas its closing router.navigate(...) to the dashboard happens AFTER
    // parseMyConfigObject() has run — so a completed navigation is a point where config is
    // guaranteed ready, rather than a timing guess.
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Keep the published screen context in step with the route. The widget prefers the
      // getScreenContext() callback below (evaluated at send time, so it can't go stale),
      // but the plain fields are what a reader without it would see.
      this.publishScreenContext();
      if (!this.initialized) {
        void this.tryInitializeChat();
      }
    });

    // Covers the "page (re)loaded with an already-valid session" case, where config/bot_id is
    // already available via the APP_INITIALIZER by the time this component boots. Deliberately
    // skipped on a fresh login — see isSessionConfigReady() — where the NavigationEnd trigger
    // above is what eventually kicks initialization off.
    if (this.isSessionConfigReady()) {
      await this.tryInitializeChat();
    }
  }

  private resetChatState(): void {
    // Invalidates any initialization attempt still in flight for the previous session — see
    // initGeneration's comment above.
    this.initGeneration++;
    this.initialized = false;
    this.shouldShowChat = false;
    this.clientId = null;
    this.flowId = null;
    delete (window as any).KX_AI_CHAT_CONFIG;
    // Cached mapping/token/org details are account-scoped; without this, logging into a
    // different account (or a different role on the same account) in the same tab would
    // otherwise reuse the previous session's cached flow/token.
    this.accountContextService.resetWorkflowState();
    localStorage.removeItem(this.jwtTokenStorageKey);
    // Must go with the access token, not merely alongside it. A session refresh token is bound to
    // a chat session and is itself the only credential needed to exchange one — so a leftover from
    // the previous login would let the next user in this tab renew, and then resume, someone
    // else's conversation. Clearing the access token alone does not prevent that.
    localStorage.removeItem(this.chatRefreshTokenStorageKey);
    this.cdr.detectChanges();
  }

  private isJwtToken(value: string): boolean {
    return typeof value === 'string' && value.split('.').length === 3;
  }

  private parseJwtPayload(token: string): Record<string, any> | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  // Mirrors get-started.component.ts's / chat-embed-element.ts's own guard of the same name —
  // keep these in sync.
  private hasUnexpiredStoredToken(): boolean {
    try {
      const stored = localStorage.getItem(this.jwtTokenStorageKey);
      if (!stored || !this.isJwtToken(stored)) return false;
      const payload = this.parseJwtPayload(stored);
      if (!payload || payload['typ'] !== 'embed') return false;
      if (payload['flow_id'] !== this.flowId || payload['client_id'] !== this.clientId) return false;
      const exp = payload['exp'];
      if (typeof exp !== 'number') return true;
      return Date.now() < exp * 1000;
    } catch {
      return false;
    }
  }

  /**
   * True once GET_MY_CONFIG has been parsed for this session (parseMyConfigObject() sets
   * configLoaded, encryptKey, bot_id and account_id together).
   *
   * This matters because the isLoggedIn gate in app.component.html mounts this component the
   * moment login-service calls setUser(), which happens BEFORE the GET_MY_CONFIG round-trip.
   * In that window _currentSessionData.encryptKey is still unset, so every API call signs its
   * X-XSRF-TOKEN with a plain MD5 instead of the encrypted one and the gateway rejects it with
   * "BAD REQUEST TNM". Worse, base.service's get() pipeline has no error handler, so the
   * rejected request never settles — the promise AccountContextService caches in _mappingPromise
   * stays pending forever and every later attempt awaits that dead promise, leaving the chat
   * broken for the rest of the session. So the call must be avoided, not merely retried.
   */
  private isSessionConfigReady(): boolean {
    return !!this.appService?._currentSessionData?.configLoaded;
  }

  private async tryInitializeChat(): Promise<void> {
    if (!this.isSessionConfigReady()) {
      return;
    }
    // Claim a new generation for this attempt. If a call to resetChatState() (or another
    // tryInitializeChat()) supersedes us while we're awaiting below, `generation` will no
    // longer match `this.initGeneration` and we bail — this can never get stuck the way a
    // simple in-flight boolean could.
    const generation = ++this.initGeneration;
    try {
      if (!this.clientId) {
        const accountDetails = await this.accountContextService.getAccountDetails();
        if (generation !== this.initGeneration) return;
        if (accountDetails?.account_id) {
          this.clientId = accountDetails.account_id;
        }
      }

      const currentUser = this.appService.getCurrentUser();
      this.userId = currentUser?.user_id;

      // No account, no chat: embed tokens are minted for the session's account only.
      if (!this.clientId) {
        return;
      }
      this.registerEmbedBridge();
      await this.updateChatMapping(generation);
    } catch (error) {
      console.error('Failed to initialize KX AI chat embed', error);
    }
  }

  private async updateChatMapping(generation: number): Promise<void> {
    const mapping = await this.accountContextService.fetchWorkflowMapping();
    if (generation !== this.initGeneration) return;

    const flowId = mapping?.flow_id || null;
    if (!flowId) {
      console.warn('No flow_id found in the workflow mapping for this account/role');
      this.flowId = null;
      this.shouldShowChat = false;
      delete (window as any).KX_AI_CHAT_CONFIG;
      return;
    }

    this.flowId = flowId;
    this.shouldShowChat = true;
    this.cdr.detectChanges();
    await this.initEmbed(generation);
  }

  private async initEmbed(generation: number): Promise<void> {
    // In-memory cache first (fastest, but wiped on a hard refresh), then the persisted
    // JWT (survives refresh) — mirrors get-started.component.ts's hasUnexpiredStoredToken()
    // guard. This isn't just an optimization: the widget bundle itself (chat-embed-element.ts)
    // may upgrade this same stored token to session-scope after create_session() succeeds.
    // Re-exchanging and overwriting it here whenever a refresh happens would downgrade an
    // already-authorized session back to a plain embed token, breaking the widget's own
    // session calls with a 403 — so we must only fetch a new one when the stored one is
    // actually missing/expired/mismatched, never unconditionally.
    let token = this.accountContextService.getEmbedToken(this.clientId, this.flowId);
    if (!token && this.hasUnexpiredStoredToken()) {
      token = localStorage.getItem(this.jwtTokenStorageKey) || '';
    }
    // Before falling back to a brand-new flow-level token, try to renew the session we already
    // have. This is the page-reload case: the in-memory cache is gone and the persisted access
    // token is usually past EMBED_TOKEN_EXPIRE_MINUTES, so without this we mint a token carrying
    // no session_id claim — and the backend's ownership check is a hard cutover on that claim
    // (chat/service.py's _require_session_ownership), so the widget's first getMessages() gets a
    // 403 and starts a blank chat. The user's history and the agent's memory, both keyed by
    // session_id, are lost for no reason. Exchanging the refresh token returns a token for the
    // SAME session, so a reload just resumes the conversation.
    if (!token) {
      token = await this.exchangeSessionRefreshToken();
      if (generation !== this.initGeneration) return;
    }
    if (!token) {
      const response: any = await this.getEmbedTokenResponse();
      if (generation !== this.initGeneration) return;
      token = response?.access_token || response?.result?.access_token || response?.resource?.[0]?.access_token;
      if (token) {
        localStorage.setItem(this.jwtTokenStorageKey, token);
      }
    }

    if (token) {
      this.initialized = true;
      this.accountContextService.setEmbedToken(this.clientId, this.flowId, token);
      this.ensureStylesheet(this.materialIconsHref, 'kx-ai-chat-material-icons');

      const screenContext = this.resolveScreenContext();
      (window as any).KX_AI_CHAT_CONFIG = {
        apiBaseUrl: this.apiBaseUrl,
        clientId: this.clientId,
        flowId: this.flowId,
        token,
        userId: this.userId || undefined,
        recaptchaSiteKey: environment.recaptcha.v3SiteKey,
        applicationName: screenContext.application_name,
        resourceName: screenContext.resource_name,
        // Re-read on every send rather than snapshotted here: the chat stays mounted across
        // navigation (this component lives at the app-shell level), so by the time a message
        // runs a flow the user is often on a different screen than when the config was written.
        getScreenContext: () => this.resolveScreenContext()
      };

      if (this.flowId && this.chatElement?.nativeElement) {
        this.chatElement.nativeElement.setAttribute('flow-id', this.flowId);
      }

      // this.ensureStylesheet(`${this.bundleBaseUrl}styles.css`, 'kx-ai-chat-styles');
      await this.loadScript(`${this.bundleBaseUrl}polyfills.js`, 'kx-ai-chat-polyfills');
      await this.loadScript(`${this.bundleBaseUrl}main.js`, 'kx-ai-chat-main');
    }
  }

  /**
   * The HIMS application/resource behind the screen the user is currently on, sent with every
   * flow execution the embedded chat runs (see the widget's base/service/screen-context.ts).
   * Backend-side it reaches every node, and fills in a himsHttpRequest node whose
   * Application/Resource were deliberately left blank on the canvas — so one flow can serve
   * whatever screen it was invoked from instead of a resource fixed at design time.
   *
   * Read from the router rather than parsed out of the URL: the resource is spelled in route
   * data for auto-CRUD/definition routes (common-resolver.service.ts) and only appears as a
   * `:resource` path param on some of them, so the URL alone doesn't reliably carry it.
   */
  private resolveScreenContext(): { application_name: string; resource_name: string } {
    let application = '';
    let resource = '';

    try {
      let route: ActivatedRoute | null = this.router.routerState.root;
      while (route) {
        const data: any = route.snapshot.data || {};
        const params: any = route.snapshot.params || {};
        if (data.application) {
          application = String(data.application);
        }
        // Route data wins over the path param: on routes that carry both (e.g.
        // ':resource/tab'), data names the real HIMS resource while the segment is just
        // whatever the caller navigated with.
        if (data.resource) {
          resource = String(data.resource);
        } else if (params.resource) {
          resource = String(params.resource);
        }
        route = route.firstChild;
      }
    } catch {
      // fall through to the URL/appService fallbacks below
    }

    // appService holds the application the current module was activated with, which also
    // covers routes assembled at runtime (buildDynamicApp/buildNocodeApp) that a snapshot
    // walk can miss.
    if (!application) {
      try {
        application = String(this.appService.getApplication() || '');
      } catch {
        application = '';
      }
    }

    const segments = (this.router.url || '').split('?')[0].split('#')[0].split('/').filter(Boolean);
    if (!application) {
      application = segments[0] ? decodeURIComponent(segments[0]) : '';
    }
    if (!resource) {
      resource = segments[1] ? decodeURIComponent(segments[1]) : '';
    }

    return { application_name: application.trim(), resource_name: resource.trim() };
  }

  /** Refreshes the static half of the published context after a navigation. */
  private publishScreenContext(): void {
    const config = (window as any).KX_AI_CHAT_CONFIG;
    if (!config) {
      return;
    }
    const screenContext = this.resolveScreenContext();
    config.applicationName = screenContext.application_name;
    config.resourceName = screenContext.resource_name;
  }

  private registerEmbedBridge(): void {
    (window as any).KX_AI_CHAT_BRIDGE = {
      serviceAction: (resource: any, action: string, application: string, resourceName: string, args?: string) => {
        // The widget asks for a fresh token by calling this with an EMPTY resource (see
        // chat.service.ts's refreshFlowLevelToken()). So intercept EMBED_TOKEN and ask for one
        // for this page's mapped flow rather than forwarding what was passed. This is what keeps
        // the chat alive for the whole logged-in session: when the token expires mid-session the
        // widget hits a 401, asks us here, and we mint a replacement transparently instead of the
        // chat staying broken until the next page reload.
        if (action === 'EMBED_TOKEN') {
          return this.refreshEmbedToken();
        }
        return firstValueFrom(
          this.baseService.action(resource, action, application, resourceName, undefined, undefined, args)
        );
      },
      get: (queryId: string, args: string, resource: string, application: string, filter?: any, responseType?: any, config?: any) => {
        return firstValueFrom(
          this.baseService.get(queryId, args, resource, application, filter, responseType, config)
        );
      },
      getByDataSource: (dataSource: any) => {
        return firstValueFrom(
          this.baseService.getByDataSource(dataSource)
        );
      }
    };
  }

  /**
   * Mints a fresh embed token for this page's mapped flow and syncs it everywhere the
   * token is read from (localStorage, the in-memory cache, and the live KX_AI_CHAT_CONFIG the
   * widget reads). Returns the raw response so the widget can extract the token itself.
   */
  private async refreshEmbedToken(): Promise<any> {
    const response: any = await this.getEmbedTokenResponse();
    const token = response?.access_token || response?.result?.access_token || response?.resource?.[0]?.access_token;
    if (token) {
      localStorage.setItem(this.jwtTokenStorageKey, token);
      this.accountContextService.setEmbedToken(this.clientId, this.flowId, token);
      if ((window as any).KX_AI_CHAT_CONFIG) {
        (window as any).KX_AI_CHAT_CONFIG.token = token;
      }
    }
    return response;
  }

  /**
   * Returns the stored session refresh token only if it can actually be used: a well-formed
   * 'embed_refresh' JWT for THIS flow/client, still inside both its own expiry and the absolute
   * ceiling on its rotation chain ('aexp' — see auth/service.py's create_embed_refresh_token).
   *
   * Checked locally first so the common "nothing to renew" case costs no network round-trip, and
   * so a token belonging to another flow/client is never even offered to the server.
   */
  private getUsableSessionRefreshToken(): string {
    try {
      const stored = localStorage.getItem(this.chatRefreshTokenStorageKey);
      if (!stored || !this.isJwtToken(stored)) return '';
      const payload = this.parseJwtPayload(stored);
      if (!payload || payload['typ'] !== 'embed_refresh') return '';
      if (payload['flow_id'] !== this.flowId || payload['client_id'] !== this.clientId) return '';
      const now = Date.now();
      const exp = payload['exp'];
      if (typeof exp === 'number' && now >= exp * 1000) return '';
      const absoluteExp = payload['aexp'];
      if (typeof absoluteExp === 'number' && now >= absoluteExp * 1000) return '';
      return stored;
    } catch {
      return '';
    }
  }

  /**
   * Exchanges the stored refresh token for an access token bound to the same chat session, and
   * syncs both halves of the result. Resolves '' on any failure so the caller just falls through
   * to minting a flow-level token.
   *
   * Goes through serviceworkflow's REFRESH_EMBED_TOKEN action rather than calling agentic
   * directly, so this page keeps a single browser-facing ingress like all its other calls. The
   * gateway forwards our real Origin/Referer (WorkflowUtility.getWorkflowHeaders), which is what
   * lets agentic still validate this page against the embed client's origin allowlist — proxying
   * without that forwarding would have made the origin check see the gateway and become useless.
   *
   * The refresh token travels in extra_data because AiAuth is generated from an external schema
   * and has no refresh_token field; the response comes back the same way.
   */
  private async exchangeSessionRefreshToken(): Promise<string> {
    const refreshToken = this.getUsableSessionRefreshToken();
    if (!refreshToken) {
      return '';
    }

    try {
      const response: any = await firstValueFrom(
        this.baseService.action(
          { extra_data: { refresh_token: refreshToken } },
          'REFRESH_EMBED_TOKEN',
          'serviceworkflow',
          'ai_auth'
        )
      );

      // Failures arrive as a non-zero errCode on an HTTP 200 (the platform convention), so the
      // body has to be inspected rather than relying on a thrown error.
      if (response?.errCode !== undefined && response.errCode !== 0) {
        // Only discard the token when the server says it is actually dead. A 401 covers eleven
        // distinct reasons and several leave the token sound — the page not being on the embed
        // client's origin allowlist, the flow un-mapped, the client disabled. Those are
        // operator-fixable, so binning the token would turn a config mistake into a permanently
        // unrecoverable conversation. kx-ai-workflow reports the verdict and the gateway appends
        // it as "recoverable=<bool>"; anything unrecognized is left alone, since keeping a token
        // costs one wasted request while wrongly dropping one costs the user their chat.
        if (/recoverable=false/i.test(String(response?.message || ''))) {
          localStorage.removeItem(this.chatRefreshTokenStorageKey);
        }
        return '';
      }

      const payload = response?.resource?.[0]?.extra_data || response?.resource?.[0] || {};
      const accessToken = payload?.access_token;
      if (!accessToken) {
        return '';
      }

      localStorage.setItem(this.jwtTokenStorageKey, accessToken);
      // The server rotates on every exchange, so the token we just sent is spent. Persisting the
      // replacement is not optional — without it the next renewal presents a superseded token.
      if (payload?.refresh_token) {
        localStorage.setItem(this.chatRefreshTokenStorageKey, payload.refresh_token);
      }
      return accessToken;
    } catch (error) {
      // Kept non-terminal on purpose: a transport/gateway failure says nothing about whether the
      // refresh token is still valid, so it is preserved for the widget to retry with.
      console.warn('Session refresh for KX AI chat failed; falling back to a new embed token', error);
      return '';
    }
  }

  /**
   * serviceworkflow mints the token only for a workflow mapped to the user's role (for their
   * account, or globally), with no embed client secret involved. The account it is minted for
   * comes back as client_id, which every stored-token check compares against.
   */
  private async getEmbedTokenResponse(): Promise<any> {
    const response: any = await firstValueFrom(
      this.baseService.action({ flow_id: this.flowId }, 'EMBED_TOKEN', 'serviceworkflow', 'ai_auth')
    );
    const clientId = response?.resource?.[0]?.client_id || response?.client_id;
    if (clientId) {
      this.clientId = clientId;
    }
    return response;
  }

  private ensureStylesheet(href: string, id: string): void {
    if (document.getElementById(id)) {
      return;
    }

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  private loadScript(src: string, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.getElementById(id) as HTMLScriptElement | null;
      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.type = 'module';
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.body.appendChild(script);
    });
  }

  ngOnDestroy(): void {
    // Clean up any global variables or event listeners if necessary
    this.authSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    delete (window as any).KX_AI_CHAT_BRIDGE;
    delete (window as any).KX_AI_CHAT_CONFIG;
    // Belt-and-braces with resetChatState(): this component is mounted behind the app shell's
    // isLoggedIn gate, so destruction means logout. If the gate tears us down before the
    // currentUser subscription fires, this is the only place the renewal credential gets dropped.
    localStorage.removeItem(this.chatRefreshTokenStorageKey);
    this.flowId = '';
  }
}
